// TABLE section — appears only when a new machine is selected. Shows the
// throat-size-appropriate table type (HP2 for 18", GS6 for 22"+).
//
// Compact UX (user-requested): instead of N stacked OptionRows (one
// per length), this is a single row: thumbnail of the selected table +
// a length dropdown + the selected option's price. Same data, ~1/5
// the vertical space.

import { useEffect, useMemo, useState } from "react";

import { SectionFrame } from "./SectionFrame";
import { ProductDetailModal } from "../ProductDetailModal";
import { tableOptionsForThroatSize } from "../../rules";
import { formatDollars, imageCandidatesFor } from "../../format";
import { useConfigurator } from "../../store";
import type { Product } from "../../api";

export function TablesSection() {
  const { state, dispatch } = useConfigurator();
  const catalog = state.catalog;
  const [detail, setDetail] = useState<Product | null>(null);

  const { options, selectedMachine } = useMemo(() => {
    if (!catalog || !state.selectedMachineId) {
      return { options: [], selectedMachine: null };
    }
    const m = catalog.byId.get(state.selectedMachineId);
    if (!m || m.category !== "machine") {
      return { options: [], selectedMachine: null };
    }
    return {
      options: tableOptionsForThroatSize(m.throatSize),
      selectedMachine: m,
    };
  }, [catalog, state.selectedMachineId]);

  if (!selectedMachine || options.length === 0) return null;

  const tableType = selectedMachine.throatSize <= 18 ? "HP2" : "GS6";
  const meta = `${tableType} table — choose any length`;

  const selectedOpt =
    options.find((o) => o.id === state.selectedTableId) ?? options[0];
  const selectedProduct = catalog!.byId.get(selectedOpt.id);

  // Tables show cash only — the monthly framing lives on the machine + the
  // Summary total.
  const priceLabel = selectedProduct
    ? formatDollars(selectedProduct.price_cents)
    : "";

  return (
    <SectionFrame
      number={2}
      label="Table"
      title="Choose your table length"
      meta={meta}
    >
      <div className="flex items-stretch gap-4 py-2">
        {/* Thumbnail of the currently-selected table. Clickable to open
            the product detail modal — same affordance as the machine
            cards' thumbnails. */}
        {selectedProduct && (
          <button
            type="button"
            onClick={() => setDetail(selectedProduct)}
            aria-label={`View ${selectedOpt.length}' ${tableType} table details`}
            className="group/thumb relative h-14 w-14 flex-none cursor-zoom-in overflow-hidden border border-line bg-mist transition hover:border-amber sm:h-16 sm:w-16"
          >
            <TableThumb
              candidates={imageCandidatesFor(selectedProduct)}
              alt={`${selectedOpt.length}' ${tableType} table`}
            />
          </button>
        )}

        {/* Dropdown + price stack. The dropdown is the click target the
            customer drives the picker with. */}
        <div className="flex flex-1 flex-col justify-center gap-1">
          <label className="font-sans text-[12px] font-semibold uppercase tracking-tag text-steel">
            Length
          </label>
          <div className="flex items-center justify-between gap-3">
            <select
              value={selectedOpt.id}
              onChange={(e) =>
                dispatch({ type: "table/select", id: e.target.value })
              }
              className="flex-1 border border-line bg-white px-3 py-2 font-display text-base font-extrabold uppercase tracking-wide text-ink focus:border-ink focus:outline-none"
            >
              {options.map((opt) => {
                const p = catalog!.byId.get(opt.id);
                const priceTag = !p
                  ? ""
                  : opt.isDefault
                    ? "Included"
                    : formatDollars(p.price_cents);
                // 8' and 11' lengths are sometimes short-supply; flag
                // so the customer doesn't get surprised by a longer
                // lead time. Inline so the warning stays bonded to
                // the option text wherever it renders.
                const delayWarning =
                  opt.length === 8 || opt.length === 11 ? " (may delay)" : "";
                return (
                  <option key={opt.id} value={opt.id}>
                    {opt.length}' {opt.type} Table{delayWarning} — {priceTag}
                  </option>
                );
              })}
            </select>
            <span className="flex-none font-display text-base font-extrabold text-ink">
              {selectedOpt.isDefault ? "Included" : priceLabel}
            </span>
          </div>
        </div>
      </div>

      <ProductDetailModal product={detail} onClose={() => setDetail(null)} />
    </SectionFrame>
  );
}

/** Mini image loader for the thumbnail next to the length dropdown.
 *  Walks the candidate list on 404; falls back to a monogram square. */
function TableThumb({ candidates, alt }: { candidates: string[]; alt: string }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => setIdx(0), [candidates.join("|")]);
  const url = candidates[idx];

  return (
    <>
      {/* Hatched placeholder behind, monogram on top of placeholder, image
          on top of monogram. Same layering pattern as OptionRow's Thumb. */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            "repeating-linear-gradient(45deg, transparent 0 8px, #E4E4E4 8px 9px)",
        }}
      />
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="select-none font-display text-xl font-extrabold uppercase tracking-tight text-amber-700">
          T
        </span>
      </div>
      {url && (
        <img
          key={url}
          src={url}
          alt={alt}
          onError={() => setIdx((i) => i + 1)}
          className="absolute inset-0 h-full w-full object-cover"
        />
      )}
      <span
        aria-hidden
        className="absolute inset-0 flex items-center justify-center bg-ink/40 opacity-0 transition group-hover/thumb:opacity-100"
      >
        <svg
          viewBox="0 0 24 24"
          className="h-5 w-5 text-white"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="11" cy="11" r="7" />
          <path d="M21 21l-4.3-4.3" />
        </svg>
      </span>
      {/* Always-visible ⓘ badge — table thumb always opens the
          product detail modal on click. Same style as OptionRow's
          badge so the cue is recognized across the configurator. */}
      <span
        aria-hidden
        className="pointer-events-none absolute bottom-1 right-1 z-10 inline-flex h-4 w-4 items-center justify-center rounded-full bg-white font-serif text-[11px] font-semibold italic leading-none text-ink shadow-[0_1px_3px_rgba(0,0,0,0.35)] sm:h-5 sm:w-5 sm:text-[13px]"
      >
        i
      </span>
    </>
  );
}
